// Tolkar en inklistrad mailsignatur till kund- och kontaktuppgifter. Signaturer
// har inget fast format (svenska och engelska etiketter, telefonnummer utan
// etikett, adresser på en eller flera rader, friskrivningar under), så själva
// tolkningen görs av modellen. Allt som går att avgöra deterministiskt —
// telefonformat, härledd webbplats, mobil kontra direktnummer — görs här efteråt
// så att resultatet inte hänger på att modellen följer varje regel.
import Fuse from 'fuse.js';
import { callClaudeJSON } from '../../utils/claudeProxy';

export const COMPANY_KEYS = [
  'name', 'orgNumber',
  'visitStreet', 'visitZip', 'visitCity',
  'postalStreet', 'postalZip', 'postalCity',
  'country', 'switchboard', 'website',
];
export const CONTACT_KEYS = [
  'firstName', 'lastName', 'title', 'department',
  'email', 'mobile', 'direct', 'linkedin',
];

// Webbmejl och leverantörsdomäner som inte säger något om arbetsgivaren.
export const GENERIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'outlook.se', 'hotmail.com',
  'hotmail.se', 'live.com', 'live.se', 'msn.com', 'icloud.com', 'me.com',
  'mac.com', 'yahoo.com', 'yahoo.se', 'ymail.com', 'aol.com', 'proton.me',
  'protonmail.com', 'gmx.com', 'gmx.net', 'mail.com', 'telia.com',
  'telia.se', 'bredband.net', 'comhem.se', 'tele2.se', 'spray.se',
]);

const str = { type: 'string' };

// Strukturerade svar garanterar giltig JSON men inte null-värden på ett sätt
// som är lätt att lita på, så schemat använder tom sträng för "saknas" och
// toResult() gör om dem till null.
const SIGNATURE_SCHEMA = {
  type: 'object',
  properties: {
    company: {
      type: 'object',
      properties: {
        name:         { ...str, description: 'Företagsnamn som det skrivs i signaturen.' },
        orgNumber:    { ...str, description: 'Organisationsnummer, bara om det står i texten.' },
        visitStreet:  { ...str, description: 'Gatuadress för besöksadress.' },
        visitZip:     str,
        visitCity:    str,
        postalStreet: { ...str, description: 'Gatuadress eller box för postadress.' },
        postalZip:    str,
        postalCity:   str,
        country:      { ...str, description: 'Land på svenska, t.ex. Sverige, Norge. Bara om det står i texten.' },
        switchboard:  { ...str, description: 'Växelnummer exakt som det står.' },
        website:      { ...str, description: 'Webbplats som den står i texten, eller härledd från e-postdomänen.' },
        inferred:     { type: 'array', items: { type: 'string', enum: ['website'] } },
      },
      required: [...COMPANY_KEYS, 'inferred'],
      additionalProperties: false,
    },
    contact: {
      type: 'object',
      properties: {
        firstName:  str,
        lastName:   str,
        title:      { ...str, description: 'Titel eller roll.' },
        department: { ...str, description: 'Avdelning, enhet eller affärsområde.' },
        email:      str,
        mobile:     { ...str, description: 'Mobilnummer exakt som det står.' },
        direct:     { ...str, description: 'Direktnummer exakt som det står.' },
        linkedin:   { ...str, description: 'Fullständig LinkedIn-URL, bara om den står utskriven.' },
      },
      required: CONTACT_KEYS,
      additionalProperties: false,
    },
    multipleSignatures: { type: 'boolean' },
  },
  required: ['company', 'contact', 'multipleSignatures'],
  additionalProperties: false,
};

const SYSTEM = `Du läser av mailsignaturer åt ett svenskt säljteam och plockar ut uppgifter om personen och dennes företag.

Svara ENDAST med JSON enligt schemat. Ett värde som inte står i texten lämnas som tom sträng. Gissa aldrig.

Företag: namn, organisationsnummer, besöksadress, postadress (gata eller box, postnummer, ort), land, växelnummer och webbplats.
- En adress som uttryckligen kallas besöksadress / visiting address / besök läggs i visit-fälten.
- En box-adress, en adress märkt postadress / postal address, och en ensam adress utan etikett läggs i postal-fälten.
- Postnummer och ort hålls isär: "411 05 Göteborg" ger postnummer "411 05" och ort "Göteborg".

Kontakt: förnamn, efternamn, titel/roll, avdelning, e-post, mobilnummer, direktnummer och LinkedIn.

Telefonnummer – avgör typ utifrån etikett i första hand:
- Mobil: Mobil, Mob, M, Mobile, Cell, Cellphone.
- Direkt: Direkt, Dir, D, Direct, DD, Tel, T, Telefon, Phone, P (när det inte uttryckligen är växeln).
- Växel: Växel, Vx, Vxl, Switchboard, Main, Reception, Huvudnummer. Växelnumret hör till företaget.
- Utan etikett: nummer som börjar på 07 eller +46 7 är mobil. Andra svenska nummer utan etikett är direktnummer.
- Skriv numret som det står; formatering sker efteråt.

Ignorera:
- juridiska friskrivningar, sekretesstext och miljöuppmaningar
- "Skickat från min iPhone" och liknande
- citerade tidigare mail och vidarebefordrade rubriker (Från:, From:, Skickat:, Sent:, Ämne:, Subject:)
- alt-text för logotyper och bilder, t.ex. [cid:...] eller [Logotyp]
- namn på sociala medier utan utskriven URL.

Webbplats: om ingen webbplats står i texten, härled den från kontaktens e-postdomän (namn@foretaget.se ger www.foretaget.se) och lägg "website" i company.inferred. Härled aldrig från generiska domäner som gmail.com, outlook.com, hotmail.com, live.se, icloud.com, yahoo.com eller telia.com. Står webbplatsen i texten är inferred tom.

Innehåller texten flera signaturer (t.ex. en mailtråd): använd den översta signaturen och sätt multipleSignatures till true. Annars false.

Signaturen kan vara på svenska eller engelska.`;

// ── Hjälpare ─────────────────────────────────────────────────────────────────

function blankToNull(v) {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
}

export function emailDomain(email) {
  const m = /@([^@\s>]+)$/.exec(String(email || '').trim().toLowerCase());
  return m ? m[1].replace(/\.$/, '') : '';
}

/** 'https://www.foretaget.se/om-oss' → 'foretaget.se' */
export function websiteDomain(url) {
  const s = String(url || '').trim().toLowerCase();
  if (!s) return '';
  const host = s.replace(/^[a-z]+:\/\//, '').split(/[/?#:\s]/)[0];
  return host.replace(/^www\d?\./, '');
}

export function isGenericDomain(domain) {
  return GENERIC_EMAIL_DOMAINS.has(String(domain || '').toLowerCase());
}

/** Webbplats härledd ur e-postadressen, eller null för generiska domäner. */
export function websiteFromEmail(email) {
  const d = emailDomain(email);
  if (!d || isGenericDomain(d)) return null;
  return `www.${d}`;
}

// Svenska riktnummer med två siffror efter nollan. 8 (Stockholm) är ensiffrigt,
// mobilprefix 7x tvåsiffrigt, övriga riktnummer tresiffriga.
const TWO_DIGIT_AREA = new Set([
  '10', '11', '13', '16', '18', '19', '21', '23', '26', '31', '33', '35', '36',
  '40', '42', '44', '46', '54', '60', '63', '90',
]);

function groupSubscriber(d) {
  switch (d.length) {
    case 5: return `${d.slice(0, 3)} ${d.slice(3)}`;
    case 6: return `${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4)}`;
    case 7: return `${d.slice(0, 3)} ${d.slice(3, 5)} ${d.slice(5)}`;
    case 8: return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    default: return d;
  }
}

/**
 * Formaterar telefonnummer som i övriga LagbasN: '+46 70 123 45 67',
 * '+46 8 123 456 78', '+46 31 123 45 67'. Utländska nummer behåller sin
 * landskod med normaliserade mellanslag. Ett nummer som inte går att tolka
 * returneras som det skrevs.
 */
export function normalizePhone(raw) {
  const original = blankToNull(raw);
  if (!original) return null;
  // Svenskt skrivsätt "+46 (0)70 ..." – nollan inom parentes ska bort.
  const cleaned = original.replace(/\(0\)/g, '');
  let digits = cleaned.replace(/\D/g, '');
  const international = /^\s*(\+|00)/.test(cleaned);
  if (international && digits.startsWith('00')) digits = digits.slice(2);

  let national;
  if (international) {
    if (!digits.startsWith('46')) {
      return '+' + cleaned.replace(/^\s*(\+|00)/, '').replace(/[^\d ]/g, ' ').replace(/\s+/g, ' ').trim();
    }
    national = digits.slice(2);
  } else if (digits.startsWith('0')) {
    national = digits.slice(1);
  } else {
    return original.replace(/\s+/g, ' ');
  }
  if (national.length < 7 || national.length > 10) return original.replace(/\s+/g, ' ');

  let areaLen = 3;
  if (national.startsWith('8')) areaLen = 1;
  else if (national.startsWith('7') || TWO_DIGIT_AREA.has(national.slice(0, 2))) areaLen = 2;
  const area = national.slice(0, areaLen);
  const subscriber = national.slice(areaLen);
  return `+46 ${area} ${groupSubscriber(subscriber)}`;
}

export function isSwedishMobile(phone) {
  return /^\+46 7[02369]/.test(normalizePhone(phone) || '');
}

// ── Svarskontroll och efterbearbetning ───────────────────────────────────────

/** Kastar om objektet inte har den form schemat lovar. */
export function validateSignatureResponse(obj) {
  const fail = msg => { throw new Error(`Ogiltigt svar från tolkningen: ${msg}`); };
  if (!obj || typeof obj !== 'object') fail('inget objekt');
  const { company, contact, multipleSignatures } = obj;
  if (!company || typeof company !== 'object') fail('company saknas');
  if (!contact || typeof contact !== 'object') fail('contact saknas');
  if (typeof multipleSignatures !== 'boolean') fail('multipleSignatures är inte boolean');
  for (const k of COMPANY_KEYS) {
    if (company[k] != null && typeof company[k] !== 'string') fail(`company.${k} är inte text`);
  }
  for (const k of CONTACT_KEYS) {
    if (contact[k] != null && typeof contact[k] !== 'string') fail(`contact.${k} är inte text`);
  }
  if (company.inferred != null && !Array.isArray(company.inferred)) fail('company.inferred är ingen lista');
  return obj;
}

/**
 * Gör modellens svar till det slutliga resultatet: tomma strängar blir null,
 * telefonnummer formateras, ett mobilnummer som hamnat som direktnummer flyttas
 * och webbplatsen härleds eller rensas enligt reglerna för e-postdomäner.
 */
export function toResult(raw) {
  validateSignatureResponse(raw);
  const company = {};
  for (const k of COMPANY_KEYS) company[k] = blankToNull(raw.company[k]);
  const contact = {};
  for (const k of CONTACT_KEYS) contact[k] = blankToNull(raw.contact[k]);

  contact.email = contact.email ? contact.email.toLowerCase() : null;
  contact.mobile = normalizePhone(contact.mobile);
  contact.direct = normalizePhone(contact.direct);
  company.switchboard = normalizePhone(company.switchboard);
  if (!contact.mobile && contact.direct && isSwedishMobile(contact.direct)) {
    contact.mobile = contact.direct;
    contact.direct = null;
  }

  let inferred = (raw.company.inferred || []).filter(k => k === 'website');
  const domain = emailDomain(contact.email);
  if (inferred.includes('website') && (!domain || isGenericDomain(domain)
      || websiteDomain(company.website) !== domain)) {
    // Modellen härledde från något annat än kontaktens företagsdomän.
    company.website = null;
    inferred = [];
  }
  if (!company.website) {
    const derived = websiteFromEmail(contact.email);
    if (derived) { company.website = derived; inferred = ['website']; }
  }
  if (!company.website) inferred = [];

  company.inferred = inferred;
  return { company, contact, multipleSignatures: raw.multipleSignatures };
}

/** Skickar texten till modellen och returnerar ett efterbearbetat resultat. */
export async function parseSignature(text) {
  const raw = await callClaudeJSON({
    model: 'claude-opus-5-5',
    max_tokens: 4000,
    schema: SIGNATURE_SCHEMA,
    system: SYSTEM,
    messages: [{ role: 'user', content: `Text att tolka:\n\n${text}` }],
  });
  return toResult(raw);
}

// ── Mappning mot LagbasN:s fält ──────────────────────────────────────────────

/**
 * Översätter resultatet till fältnamnen i crmCompanies och contacts.
 * aiKeys = formulärfält som fylldes av tolkningen, inferredKeys = fält som
 * härleddes (just nu bara webbplatsen).
 */
export function toFormValues(result) {
  const { company: c, contact: p } = result;
  const company = {
    name: c.name, orgNumber: c.orgNumber, website: c.website, phone: c.switchboard,
    address: c.postalStreet, zip: c.postalZip, city: c.postalCity,
    visitStreet: c.visitStreet, visitZip: c.visitZip, visitCity: c.visitCity,
    country: c.country,
  };
  const contact = {
    name: [p.firstName, p.lastName].filter(Boolean).join(' ') || null,
    role: p.title, department: p.department, email: p.email,
    mobile: p.mobile, phone: p.direct, linkedin: p.linkedin,
  };
  const strip = o => Object.fromEntries(Object.entries(o).filter(([, v]) => v));
  return {
    company: strip(company),
    contact: strip(contact),
    inferredKeys: c.inferred.includes('website') ? ['website'] : [],
  };
}

// ── Dubblettkontroll ─────────────────────────────────────────────────────────

const LEGAL_SUFFIX = /\b(ab|aktiebolag|publ|hb|kb|ek\.?\s*för\.?|ekonomisk förening|as|asa|a\/s|oy|gmbh|ltd|limited|inc|llc|group|sverige|sweden)\b/gi;

export function companyNameKey(name) {
  return String(name || '').toLowerCase()
    .replace(/[()]/g, ' ').replace(LEGAL_SUFFIX, ' ')
    .replace(/[^\p{L}\p{N} ]/gu, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Letar befintliga kunder som kan vara samma företag. Domänträff (exakt, mot
 * kundens webbplats) väger tyngst, därefter liknande namn via fuse.js.
 * Returnerar [{ company, reason: 'domain' | 'name', score }] med bästa först.
 */
export function findCompanyMatches(companies, { name, website, email }) {
  const domains = new Set(
    [websiteDomain(website), emailDomain(email)].filter(d => d && !isGenericDomain(d))
  );
  const out = new Map();
  if (domains.size) {
    for (const c of companies) {
      const d = websiteDomain(c.website);
      if (d && domains.has(d)) out.set(c.id, { company: c, reason: 'domain', score: 0 });
    }
  }
  const key = companyNameKey(name);
  if (key) {
    const fuse = new Fuse(
      companies.map(c => ({ c, key: companyNameKey(c.name) })),
      { keys: ['key'], threshold: 0.3, includeScore: true, ignoreLocation: true },
    );
    for (const r of fuse.search(key).slice(0, 5)) {
      if (!out.has(r.item.c.id)) out.set(r.item.c.id, { company: r.item.c, reason: 'name', score: r.score });
    }
  }
  return [...out.values()].sort((a, b) =>
    (a.reason === b.reason ? a.score - b.score : a.reason === 'domain' ? -1 : 1));
}

function same(a, b) {
  const norm = v => String(v || '').toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '')
    .replace(/[\s\-/.]+$/g, '').replace(/[\s-]/g, '');
  return norm(a) === norm(b);
}

/**
 * Jämför inkommande värden med en befintlig post. Fält som är tomma på posten
 * blir 'fill', fält med ett annat värde blir 'conflict', lika värden hoppas över.
 */
export function mergePlan(existing, incoming, labels) {
  return Object.keys(labels)
    .filter(k => incoming[k])
    .map(k => {
      const cur = existing?.[k];
      if (!cur || !String(cur).trim()) return { key: k, label: labels[k], kind: 'fill', next: incoming[k] };
      if (same(cur, incoming[k])) return null;
      return { key: k, label: labels[k], kind: 'conflict', current: cur, next: incoming[k] };
    })
    .filter(Boolean);
}

export const COMPANY_FIELD_LABELS = {
  orgNumber: 'Org.nr', website: 'Webbplats', phone: 'Växel',
  address: 'Postadress', zip: 'Postnr', city: 'Ort',
  visitStreet: 'Besöksadress', visitZip: 'Besök postnr', visitCity: 'Besök ort',
  country: 'Land',
};

export const CONTACT_FIELD_LABELS = {
  name: 'Namn', role: 'Roll', department: 'Avdelning', mobile: 'Mobil',
  phone: 'Direktnummer', linkedin: 'LinkedIn',
};
